import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getElementsSortedBy } from 'utils/dates';
import { allPass } from 'ramda';
export const useQuery = () => new URLSearchParams(useLocation().search);
/**
 * Get the size of an element
 * @param deps dependencies for the useEffect hook
 */
export const useClientRect = (deps = []) => {
    const [clientRect, setClientRect] = useState({});
    const ref = useRef(null);
    useEffect(() => {
        if (!ref.current)
            return;
        setClientRect(ref.current.getBoundingClientRect());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
    return [ref, clientRect];
};
const filterByCreationDate = ([fromDate, toDate = new Date()]) => ({ createdAt }) => fromDate.getTime() < createdAt && toDate.getTime() > createdAt;
const filterByName = (searchText) => ({ name }) => name.toLowerCase().includes(searchText.toLowerCase());
const filterByTypeId = (selectedProtocolType) => (protocol) => protocol.type?.id === selectedProtocolType ||
    selectedProtocolType === 'All types';
const defaultToTrue = (fn, value) => (!value ? () => true : fn(value));
export const useProtocolFilters = (protocols = [], sortMethod = 'latest') => {
    const [dateRange, setDateRange] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [selectedType, setSelectedType] = useState('All types');
    const [selectedSortMethod, setSelectedSortMethod] = useState(sortMethod);
    const getFilteredProtocols = allPass([
        defaultToTrue(filterByCreationDate, dateRange),
        defaultToTrue(filterByName, searchText),
        defaultToTrue(filterByTypeId, selectedType),
    ]);
    const filteredProtocols = getElementsSortedBy(protocols.filter(getFilteredProtocols), 'createdAt', selectedSortMethod === 'latest');
    return {
        filteredProtocols,
        setDateRange,
        setSearchText,
        setSelectedType,
        setSelectedSortMethod,
        dateRange,
        searchText,
        selectedType,
        selectedSortMethod,
    };
};
export function useDebouncedValue(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(timeout);
        };
    }, [value, delay]);
    return debouncedValue;
}
/**
 * Hook for tracking previous value
 */
export function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
}
/**
 * Hook for boolean toggle state
 */
export function useToggle(initialValue = false) {
    const [value, setValue] = useState(initialValue);
    const toggle = useCallback(() => setValue(v => !v), []);
    const set = useCallback((v) => setValue(v), []);
    return [value, toggle, set];
}
export function useAsync(asyncFn, deps = []) {
    const [state, setState] = useState({
        data: null,
        loading: true,
        error: null,
    });
    useEffect(() => {
        let mounted = true;
        setState(prev => ({ ...prev, loading: true }));
        asyncFn()
            .then(data => {
            if (mounted) {
                setState({ data, loading: false, error: null });
            }
        })
            .catch(error => {
            if (mounted) {
                setState({ data: null, loading: false, error });
            }
        });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
    return state;
}
/**
 * Hook for handling click outside of an element
 */
export function useClickOutside(callback) {
    const ref = useRef(null);
    useEffect(() => {
        const handleClick = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                callback();
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [callback]);
    return ref;
}
/**
 * Hook for observing element size changes using ResizeObserver.
 * Replacement for @reach/rect which doesn't support React 18.
 */
export function useResizeObserver(ref, options = { observe: true }) {
    const observe = typeof options === 'boolean' ? options : (options.observe ?? true);
    const [rect, setRect] = useState(null);
    useEffect(() => {
        if (!ref.current || !observe)
            return;
        const element = ref.current;
        // Initial measurement
        setRect(element.getBoundingClientRect());
        const observer = new ResizeObserver((entries) => {
            if (entries[0]) {
                setRect(entries[0].contentRect);
            }
        });
        observer.observe(element);
        return () => {
            observer.disconnect();
        };
    }, [ref, observe]);
    return rect;
}
